<template>
  <select-options-wrapper-fi
    ref="wrapper"
    :name="nameCleaned"
    :has-selection="hasSelection"
    :required="required"
    @close="emit('close')"
    @clear-selection="clearSelection"
  >
    <div
      v-for="element in elements"
      :key="`${element.key || element.name || element.text}${element.selected}`"
      :class="{
        'facet-list-fi__item': true,
        'facet-list-fi__item--checked': element.selected,
      }"
    >
      <checkbox-fi
        v-model="element.selected"
        :disabled="element.disabled"
        :class="{
          'checkbox-fi--hidden-checkbox': !multiselect,
          'checkbox-fi--full-width': true,
          'checkbox-fi--no-margin': true,
          'facet-list-fi__checkbox': true,
          'facet-list-fi__checkbox--disabled': element.disabled,
        }"
        :variant="element.disabled ? 'disabled' : null"
        :name="element.key || element.name"
        @update:model-value="(value) => onChange(value, element)"
      >
        <div
          :class="{
            'facet-list-fi__checkbox-content': true,
            'facet-list-fi__checkbox-content--hidden-checkbox': !multiselect,
          }"
        >
          <span data-test="facet-list-checkbox">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="element.name || element.text" />

            <span
              v-if="unit"
              class="facet-list-fi__unit"
            >
              {{ unit }}
            </span>

            <span
              v-if="
                element.count || element.count === 0 || element.totalHits || element.totalHits === 0
              "
              class="facet-list-fi__count"
            >
              ({{ element.count !== undefined ? element.count : element.totalHits }})
            </span>
          </span>

          <icon-fi
            v-if="!isSM && element.selected"
            icon="check"
          />
        </div>
      </checkbox-fi>
    </div>
  </select-options-wrapper-fi>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';

import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import SelectOptionsWrapperFi from '@/components/atoms/select-options-wrapper-fi/SelectOptionsWrapperFi.vue';
import factFinderNgNameCleaner from '@/components/search-fact-finder-fi/factFinderNgNameCleaner';
import { useBreakpoints } from '@/composables/breakpoint';

const CheckboxFi = defineAsyncComponent(
  () => import('@/components/atoms/checkbox-fi/CheckboxFi.vue'),
);

interface FacetElement {
  key?: string;
  name?: string;
  text?: string;
  selected?: boolean;
  disabled?: boolean;
  count?: number;
  totalHits?: number;
}

interface Props {
  name: string;
  elements: FacetElement[];
  multiselect?: boolean;
  unit?: string;
  required?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'change', payload: { value: boolean; element: FacetElement }): void;
  (e: 'clear-selection'): void;
}

const props = withDefaults(defineProps<Props>(), {
  multiselect: false,
  unit: '',
});

const emit = defineEmits<Emits>();

const { isSM } = useBreakpoints();

const wrapper = ref<InstanceType<typeof SelectOptionsWrapperFi> | null>(null);

const nameCleaned = computed(() => factFinderNgNameCleaner(props.name));

const hasSelection = computed(() => props.elements.some((element) => element.selected));

const close = async () => {
  wrapper.value?.close();
};

const onChange = (value: boolean, element: FacetElement) => {
  emit('change', { value, element });
  if (!props.multiselect) {
    close();
  }
};

const clearSelection = () => {
  emit('clear-selection');
  close();
};

defineExpose({
  close,
});
</script>

<style scoped src="./facet-list-fi" />
